import React, { useState, useEffect } from 'react';
import { Plus, X, ArrowUp, ArrowDown, Film, Image, Clock, AlertTriangle } from 'lucide-react';
import { Asset } from '../types/assets';
import { Playlist, PlaylistItem } from '../types/playlists';
import { playlistsApi } from '../services/playlistsApi';
import { assetsApi } from '../services/assetsApi';
import AssetSelectorModal from './AssetSelectorModal';

interface PlaylistManagerProps {
  playlist?: Playlist;
  onSave: (playlist: Playlist) => void;
  onCancel: () => void;
}

const PlaylistManager: React.FC<PlaylistManagerProps> = ({
  playlist,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(playlist?.playlistName || 'New Playlist');
  const [items, setItems] = useState<PlaylistItem[]>(playlist?.playlistItems || []);
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const [availableAssets, setAvailableAssets] = useState<Asset[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const assets = await assetsApi.getAllAssets();
        setAvailableAssets(assets);
      } catch (error) {
        console.error('Error fetching assets:', error);
        setError('Failed to load assets');
      }
    };
    fetchAssets();
  }, []);

  const handleAssetSelect = (asset: Asset) => {
    const newItem: PlaylistItem = {
      playlistItemId: Date.now(),
      assetId: asset.assetId,
      displayOrder: items.length + 1,
      displayDuration: asset.duration || 30,
      active: true,
      asset
    };
    setItems([...items, newItem]);
    setShowAssetSelector(false);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    if (direction === 'up' && index > 0) {
      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
    } else if (direction === 'down' && index < newItems.length - 1) {
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    }
    
    newItems.forEach((item, idx) => {
      item.displayOrder = idx + 1;
    });
    
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    newItems.forEach((item, idx) => {
      item.displayOrder = idx + 1;
    });
    setItems(newItems);
  };

  // PlaylistManager.tsx - Modified handleSave function
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a playlist name');
      return;
    }

    if (items.length === 0) {
      setError('Please add at least one item to the playlist');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const formattedItems = items.map(item => {
        const { playlistId, asset, ...rest } = item;
        return {
          ...rest,
          assetId: item.assetId || 0,
          displayDuration: item.displayDuration || 30,
          active: true
        };
      });

      if (playlist?.playlistId) {
        // Update existing playlist
        await playlistsApi.updatePlaylist(playlist.playlistId, {
          playlistName: name,
          active: true,
          playlistItems: formattedItems
        });
      } else {
        // Create new playlist
        await playlistsApi.createPlaylist({
          playlistName: name,
          createdBy: 1,
          active: true,
          playlistItems: formattedItems.map(item => ({
            assetId: item.assetId,
            displayOrder: item.displayOrder,
            displayDuration: item.displayDuration,
            active: true
          }))
        });
      }

      // Call the onSave callback to update the parent component
      onSave({
        playlistId: playlist?.playlistId || 0,
        playlistName: name,
        createdBy: playlist?.createdBy || 1,
        createdDateTime: playlist?.createdDateTime || new Date(),
        active: true,
        playlistItems: items
      });
    } catch (err) {
      setError('Failed to save playlist. Please try again.');
      console.error('Error saving playlist:', err);
    } finally {
      setSaving(false);
    }
  };
  
  const updateItemDuration = (index: number, duration: number) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      displayDuration: duration
    };
    setItems(newItems);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {playlist ? 'Edit Playlist' : 'Create Playlist'}
          </h1>
        </div>
        <button 
          onClick={() => setShowAssetSelector(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Content
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="text-xl font-semibold bg-transparent border-none focus:outline-none w-full"
            placeholder="Enter playlist name"
          />
        </div>

        {error && (
          <div className="p-4 bg-red-50 border-b">
            <div className="flex items-center text-red-700">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {error}
            </div>
          </div>
        )}

        <div className="p-4">
          {items.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No items in playlist. Click "Add Content" to get started.
            </div>
          ) : (
            items.map((item, index) => (
              <div
                key={item.playlistItemId}
                className="flex items-center bg-gray-50 rounded-lg p-4 mb-3 border border-gray-200"
              >
                <div className="flex flex-col mr-4">
                  <button 
                    onClick={() => moveItem(index, 'up')}
                    disabled={index === 0}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                  >
                    <ArrowUp className="w-4 h-4 text-gray-500" />
                  </button>
                  <button 
                    onClick={() => moveItem(index, 'down')}
                    disabled={index === items.length - 1}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                  >
                    <ArrowDown className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center">
                    {item.asset?.fileType === 'video' ? (
                      <Film className="w-5 h-5 mr-2 text-blue-500" />
                    ) : (
                      <Image className="w-5 h-5 mr-2 text-green-500" />
                    )}
                    <h3 className="font-medium">{item.asset?.fileName}</h3>
                  </div>
                  
                  <div className="flex items-center mt-2">
                    <Clock className="w-4 h-4 mr-1 text-gray-500" />
                    <input
                      type="number"
                      value={item.displayDuration}
                      onChange={(e) => updateItemDuration(index, parseInt(e.target.value))}
                      className="w-16 px-2 py-1 border rounded text-sm"
                      min="1"
                    />
                    <span className="ml-1 text-sm text-gray-500">seconds</span>
                  </div>
                </div>

                <button 
                  onClick={() => removeItem(index)}
                  className="p-2 hover:bg-gray-200 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <div className="flex justify-end space-x-4">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name || items.length === 0}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <AssetSelectorModal
        isOpen={showAssetSelector}
        onClose={() => setShowAssetSelector(false)}
        onSelect={handleAssetSelect}
        assets={availableAssets}
      />
    </div>
  );
};

export default PlaylistManager;